import React, { useEffect, useState } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { Card } from "@/components/ui/card";
import { Table, TableHeader, TableHead, TableRow, TableBody, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DataToolbar, StatusBadge } from "@/components/data/Common";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";

export default function Users() {
  const [list, setList] = useState([]);
  const [projects, setProjects] = useState([]);
  const [q, setQ] = useState("");
  const [assignOpen, setAssignOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [sel, setSel] = useState([]);

  const load = () => api.get("/users").then((r) => setList(r.data));
  useEffect(() => { load(); api.get("/projects").then((r) => setProjects(r.data)); }, []);

  const openAssign = (u) => { setActive(u); setSel(u.project_ids || []); setAssignOpen(true); };
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const saveAssign = async () => {
    try {
      await api.patch(`/users/${active.id}/projects`, { project_ids: sel });
      toast.success("Project assignment updated");
      setAssignOpen(false); load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const pname = (id) => projects.find((p) => p.id === id)?.name || id?.slice(0, 6);
  const filt = list.filter((u) => !q || `${u.name} ${u.email} ${u.role}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <AppLayout title="Users" subtitle="User accounts, roles & project assignment">
      <DataToolbar search={q} setSearch={setQ} searchTestId="users-search-input" />
      <Card className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Assigned Projects</TableHead><TableHead className="text-right">Actions</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {filt.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.email}`}>
                <TableCell className="font-medium">{u.name}</TableCell>
                <TableCell className="font-mono text-xs">{u.email}</TableCell>
                <TableCell><StatusBadge status={u.role} /></TableCell>
                <TableCell className="text-xs">
                  {["super_admin", "company_admin"].includes(u.role)
                    ? <span className="text-slate-400">All projects (unrestricted)</span>
                    : (u.project_ids?.length ? u.project_ids.map(pname).join(", ") : <span className="text-red-500">Unassigned</span>)}
                </TableCell>
                <TableCell className="text-right">
                  {!["super_admin", "company_admin"].includes(u.role) && (
                    <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => openAssign(u)} data-testid={`user-assign-${u.email}`}>Assign Projects</Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent data-testid="user-assign-dialog">
          <DialogHeader><DialogTitle>Assign Projects — {active?.name}</DialogTitle></DialogHeader>
          <div className="text-xs text-slate-500 mb-2">{active?.role === "customer" ? "Customer: first selected project is used as their portal scope." : "User will only access data for the selected projects."}</div>
          <div className="space-y-2">
            {projects.map((p) => (
              <label key={p.id} className="flex items-center gap-2 rounded-md border px-3 py-2 cursor-pointer hover:bg-slate-50" data-testid={`assign-project-${p.id}`}>
                <Checkbox checked={sel.includes(p.id)} onCheckedChange={() => toggle(p.id)} />
                <span className="text-sm">{p.name} <span className="text-slate-400 text-xs">· {p.client}</span></span>
              </label>
            ))}
          </div>
          <DialogFooter><Button onClick={saveAssign} className="bg-sky-900 hover:bg-sky-950" data-testid="user-assign-save">Save assignment</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
